import React, { useState } from "react";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Typography,
  Tab,
  Checkbox,
  FormGroup,
  Switch,
  Stack,
  Tooltip,
  TextField,
} from "@mui/material";
import { Field } from "formik";
import { CKEditor } from "ckeditor4-react";
import {
  CK_FONT_SIZE_OPTIONS,
  UploadComponent,
} from "app/pages/presentation/module/form/types";
import { PRODUCT_LABELS } from "app/utils/constants/settings";
import { stringToBoolean } from "app/utils/appHelpers";
import IndividualSettingsTab from "./IndividualSettingsTab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO } from "date-fns";
import { format } from "date-fns";

function KitchenPrintingSection({
  values,
  setFieldValue,
  t,
  datavisionPosEnabled,
  kitchenPrintingEnabled,
  kitchenPrintersGrouped,
}) {
  const active =
    datavisionPosEnabled === true && kitchenPrintingEnabled === true;
  const pk = values.productKitchenPrinting || { mode: "none", printerIds: [] };
  const printerIds = Array.isArray(pk.printerIds) ? pk.printerIds : [];
  const isNone = printerIds.length === 0;

  if (datavisionPosEnabled !== true) {
    return null;
  }

  const setNone = () => {
    setFieldValue("productKitchenPrinting", { mode: "none", printerIds: [] });
  };

  const setPrinterIds = (ids) => {
    const next = ids.filter((id) => id != null && String(id).trim() !== "");
    setFieldValue("productKitchenPrinting", {
      mode: next.length > 0 ? "selected" : "none",
      printerIds: next,
    });
  };

  const handleNoPrintChange = (e) => {
    if (e.target.checked) setNone();
  };

  const handlePrinterChange = (printerId, checked) => {
    if (checked) {
      setPrinterIds([...new Set([...printerIds, printerId])]);
    } else {
      const next = printerIds.filter((id) => id !== printerId);
      setPrinterIds(next);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t("pages.dsokProducts.kitchenPrintingTitle")}
      </Typography>
      {!active && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {!datavisionPosEnabled
            ? t("pages.dsokProducts.kitchenPrintingDisabledNoPos")
            : t("pages.dsokProducts.kitchenPrintingDisabledNoKitchen")}
        </Typography>
      )}
      <FormGroup sx={{ gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isNone}
              onChange={handleNoPrintChange}
              disabled={!active}
            />
          }
          label={t("pages.dsokProducts.kitchenPrintingNoPrint")}
        />
        {active && kitchenPrintersGrouped.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 2, mt: 0.5 }}
          >
            {t("pages.dsokProducts.kitchenPrintingNoPrinters")}
          </Typography>
        )}
        {active &&
          kitchenPrintersGrouped.map(
            ({ workspaceKey, workspaceName, printers }) => (
              <Box key={workspaceKey} sx={{ ml: 2, mt: 1 }}>
                <Typography
                  variant="caption"
                  color="gray"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {workspaceName}
                </Typography>
                {printers.map(({ id, name }) => (
                  <FormControlLabel
                    key={id}
                    control={
                      <Checkbox
                        size="small"
                        checked={printerIds.includes(id)}
                        onChange={(e) =>
                          handlePrinterChange(id, e.target.checked)
                        }
                      />
                    }
                    label={name || id}
                  />
                ))}
              </Box>
            ),
          )}
      </FormGroup>
    </Box>
  );
}

const PARAM_FIELDS = [
  {
    key: "volumeLiters",
    labelKey: "pages.dsokProducts.volumeLiters",
    unit: "l",
  },
  { key: "weightKg", labelKey: "pages.dsokProducts.weightKg", unit: "kg" },
  { key: "lengthMm", labelKey: "pages.dsokProducts.lengthMm", unit: "mm" },
  { key: "widthMm", labelKey: "pages.dsokProducts.widthMm", unit: "mm" },
  { key: "heightMm", labelKey: "pages.dsokProducts.heightMm", unit: "mm" },
];

function formatParamValue(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : "";
}

function parseParamValue(raw) {
  const s = raw == null ? "" : String(raw).trim();
  if (s === "") return null;
  if (s.endsWith(".") || s.endsWith(",")) return null;
  const n = Number(s.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? Number(n.toFixed(2)) : null;
}

function ParametersSection({ values, setFieldValue, t }) {
  const params = values.productParameters || {};
  const [raw, setRaw] = React.useState(() =>
    PARAM_FIELDS.reduce(
      (acc, { key }) => ({ ...acc, [key]: formatParamValue(params[key]) }),
      {},
    ),
  );
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    const next = PARAM_FIELDS.reduce(
      (acc, { key }) => ({ ...acc, [key]: formatParamValue(params[key]) }),
      {},
    );
    setRaw((prev) =>
      JSON.stringify(prev) !== JSON.stringify(next) ? next : prev,
    );
  }, [
    params.volumeLiters,
    params.weightKg,
    params.lengthMm,
    params.widthMm,
    params.heightMm,
  ]);

  const handleChange = (key, inputValue) => {
    setRaw((prev) => ({ ...prev, [key]: inputValue }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    if (inputValue === "" || inputValue == null) {
      setFieldValue("productParameters", {
        ...params,
        [key]: null,
      });
      return;
    }
    const n = parseParamValue(inputValue);
    if (n !== null) {
      setFieldValue("productParameters", { ...params, [key]: n });
    }
  };

  const handleBlur = (key) => {
    const inputValue = raw[key];
    const n = parseParamValue(inputValue);
    if (inputValue !== "" && inputValue != null && n === null) {
      setErrors((prev) => ({
        ...prev,
        [key]: t("pages.dsokProducts.parametersInvalidNumber", {
          defaultValue: "Zadajte platné číslo",
        }),
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setFieldValue("productParameters", { ...params, [key]: n });
    setRaw((prev) => ({ ...prev, [key]: formatParamValue(n) }));
  };

  const renderField = (key, labelKey, unit) => (
    <Grid item xs={12} sm={6} md={4} key={key}>
      <Div sx={{ mb: 1 }}>
        <JumboTextField
          fullWidth
          type="number"
          name={`productParameters.${key}`}
          label={t(labelKey)}
          value={raw[key] ?? ""}
          onChange={(e) => handleChange(key, e.target.value)}
          onBlur={() => handleBlur(key)}
          error={Boolean(errors[key])}
          helperText={errors[key]}
          InputProps={{
            inputProps: { min: 0, step: 0.01 },
            endAdornment: (
              <InputAdornment position="end">{unit}</InputAdornment>
            ),
          }}
        />
      </Div>
    </Grid>
  );

  return (
    <Stack width="100%" sx={{ py: 1 }}>
      <Grid container spacing={2}>
        {PARAM_FIELDS.slice(0, 2).map(({ key, labelKey, unit }) =>
          renderField(key, labelKey, unit),
        )}
        <Grid item xs={12} sm={6} md={4} />
        {PARAM_FIELDS.slice(2, 5).map(({ key, labelKey, unit }) =>
          renderField(key, labelKey, unit),
        )}
      </Grid>
    </Stack>
  );
}

const MainTab = ({
  values,
  setFieldValue,
  productLanguages,
  selectedLanguage,
  setSelectedLanguage,
  defaultLanguage,
  t,
  locations,
  customerUiSettings,
  accessSystemEnabled,
  isGastro,
  allergensList,
  basicSettingsSubTab: basicSettingsSubTabProp,
  setBasicSettingsSubTab: setBasicSettingsSubTabProp,
  datavisionPosEnabled,
  kitchenPrintingEnabled,
  kitchenPrintersGrouped,
  ticketPrintersGrouped,
  ticketPrintersLoading = false,
  cashRegisterCode = "",
}) => {
  const randomNumber = Math.random();
  const [basicSettingsSubTabInternal, setBasicSettingsSubTabInternal] =
    useState("basicInfo");
  const basicSettingsSubTab =
    basicSettingsSubTabProp !== undefined
      ? basicSettingsSubTabProp
      : basicSettingsSubTabInternal;
  const setBasicSettingsSubTab =
    setBasicSettingsSubTabProp !== undefined
      ? setBasicSettingsSubTabProp
      : setBasicSettingsSubTabInternal;

  return (
    <Stack
      direction="row"
      width="100%"
      spacing={0}
      sx={{ alignItems: "stretch" }}
    >
      <TabContext value={basicSettingsSubTab}>
        <Box
          sx={{
            borderRight: 1,
            borderColor: "divider",
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TabList
            orientation="vertical"
            onChange={(_, val) => setBasicSettingsSubTab(val)}
            sx={{
              flex: 1,
              "& .MuiTabList-flexContainer": {
                flexDirection: "column",
                alignItems: "stretch",
              },
            }}
          >
            <Tab
              label={t("pages.dsokProducts.basicInfo")}
              value="basicInfo"
              sx={{ textTransform: "none", alignItems: "flex-start" }}
            />
            <Tab
              label={t("pages.products.description")}
              value="description"
              sx={{ textTransform: "none", alignItems: "flex-start" }}
            />
            <Tab
              label={t("pages.dsokProducts.parameters")}
              value="parameters"
              sx={{ textTransform: "none", alignItems: "flex-start" }}
            />
            {accessSystemEnabled === true && (
              <Tab
                label={t("pages.dsokProducts.individualSettings")}
                value="individualSettings"
                sx={{ textTransform: "none", alignItems: "flex-start" }}
              />
            )}
          </TabList>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
          <TabPanel value="basicInfo" sx={{ p: 0, height: "100%" }}>
            <Stack width="100%">
              <Stack direction="row" gap={2} pl={1}>
                <Grid container columnSpacing={2}>
                  <Grid container columnSpacing={2} width="30%">
                    <Grid item md={4} xs={12} lg={3}>
                      {!values.customProductImageUrl && (
                        <UploadComponent
                          name="image"
                          setFieldValue={setFieldValue}
                          value={values.image}
                        />
                      )}
                      {values.customProductImageUrl && (
                        <>
                          <a
                            href={
                              values.customProductImageUrl +
                              "?random=" +
                              randomNumber
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={
                                values.customProductImageUrl +
                                "?random=" +
                                randomNumber
                              }
                              alt={values.productName}
                              style={{
                                width: "150px",
                                objectFit: "contain",
                              }}
                            />
                          </a>
                        </>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item md={8} xs={12} lg={6}>
                    <Grid item md={8} xs={12} lg={12}>
                      <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Field
                              name={`isActive`}
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(`isActive`, e.target.checked)
                              }
                            />
                          }
                          label={t("pages.products.active")}
                          checked={stringToBoolean(values.isActive)}
                        />
                        <FormControlLabel
                          control={
                            <Field
                              name={`isGlutenFree`}
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(`isGlutenFree`, e.target.checked)
                              }
                            />
                          }
                          label={t("pages.dsokProducts.glutenFree", {
                            defaultValue: "Bezlepkové",
                          })}
                          checked={stringToBoolean(values.isGlutenFree)}
                        />
                        <Tooltip
                          arrow
                          title={t(
                            "pages.dsokProducts.displayValidityDateHelp",
                            {
                              defaultValue:
                                "Ak je aktívne, pri produkte sa zobrazí datepicker s možnosťou výberu dátumu. Využitie napríklad pri permanentkách.",
                            }
                          )}
                        >
                          <Box component="span">
                            <FormControlLabel
                              control={
                                <Field
                                  name={`displayValidityDate`}
                                  component={Switch}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `displayValidityDate`,
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label={t(
                                "pages.dsokProducts.displayValidityDate",
                                { defaultValue: "Zobraziť platnosť od" }
                              )}
                              checked={stringToBoolean(
                                values.displayValidityDate
                              )}
                            />
                          </Box>
                        </Tooltip>
                      </Div>
                    </Grid>
                    <Grid item md={8} xs={12} lg={12}>
                      <Grid container columnSpacing={2}>
                        <Grid item xs={12} md={8}>
                          <Stack
                            spacing={2}
                            sx={{ mt: 2, mb: 1, mx: 0 }}
                          >
                            {(Array.isArray(productLanguages) &&
                            productLanguages.length > 0
                              ? productLanguages
                              : [defaultLanguage]
                            ).map((lang) => (
                              <JumboTextField
                                key={lang}
                                fullWidth
                                required={lang === defaultLanguage}
                                name={`productName.${lang}`}
                                label={`${t("pages.blocks.moduleName")} (${t(
                                  `languages.${lang}`,
                                )})`}
                              />
                            ))}
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Div sx={{ mt: 2, mb: 1 }}>
                            <JumboTextField
                              select
                              fullWidth
                              name="labels"
                              label={t("pages.dsokProducts.labels")}
                            >
                              {PRODUCT_LABELS.map((label) => {
                                return (
                                  <MenuItem key={label.code} value={label.code}>
                                    {label.name}
                                  </MenuItem>
                                );
                              })}
                            </JumboTextField>
                          </Div>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item md={8} xs={12} lg={12}>
                      <Grid container columnSpacing={2}>
                        <Grid item xs={12} md={6}>
                          <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                            <JumboTextField
                              fullWidth
                              name={`_id`}
                              label={t("pages.dsokProducts.productId")}
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          </Div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                            <JumboTextField
                              fullWidth
                              name={`sku`}
                              label={t("pages.dsokProducts.sku")}
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          </Div>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item md={12} xs={12} lg={12}></Grid>

                  <Grid item md={2} xs={6} lg={2}>
                    <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                      <JumboTextField
                        fullWidth
                        name={`customProductId`}
                        label={t("pages.dsokProducts.customProductId")}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Div>
                  </Grid>
                  {cashRegisterCode ? (
                    <Grid item md={2} xs={6} lg={2}>
                      <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                        <TextField
                          fullWidth
                          label={t("pages.dsokProducts.plu", {
                            defaultValue: "PLU, skladová karta",
                          })}
                          value={cashRegisterCode}
                          InputProps={{ readOnly: true }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Div>
                    </Grid>
                  ) : null}
                  <Grid item md={2} xs={6} lg={2}>
                    <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label={t("pages.dsokProducts.expirationDate")}
                          sx={{ width: "100%" }}
                          slotProps={{
                            textField: {
                              InputLabelProps: { shrink: true },
                              placeholder: "",
                              helperText: t(
                                "pages.dsokProducts.expirationDateHelp",
                              ),
                            },
                            actionBar: {
                              actions: ["clear"],
                            },
                          }}
                          value={
                            values.expirationDate
                              ? typeof values.expirationDate === "string"
                                ? parseISO(values.expirationDate)
                                : values.expirationDate
                              : null
                          }
                          onChange={(newValue) =>
                            setFieldValue(
                              "expirationDate",
                              newValue instanceof Date &&
                                !Number.isNaN(newValue.getTime())
                                ? format(newValue, "yyyy-MM-dd")
                                : null,
                            )
                          }
                        />
                      </LocalizationProvider>
                    </Div>
                  </Grid>
                  {values.configuration && (
                    <Grid item md={6} xs={12}>
                      <Div sx={{ mb: 1, mt: 2, mx: 0 }}>
                        <JumboTextField
                          fullWidth
                          name={`configuration`}
                          label={t("pages.dsokProducts.configuration")}
                        />
                      </Div>
                    </Grid>
                  )}
                  <Grid item md={12} xs={12} lg={12}></Grid>
                  <Grid item md={12} xs={12} lg={12}>
                    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              Boolean(values.tempParameter?.autoAddBindingsToOrder)
                            }
                            onChange={(e) =>
                              setFieldValue(
                                "tempParameter.autoAddBindingsToOrder",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t(
                          "pages.dsokProducts.autoAddBindingsToOrder",
                          { defaultValue: "Auto-add bindings to order (dočasné)" }
                        )}
                      />
                    </Div>
                  </Grid>
                  {customerUiSettings?.enableStocks && (
                    <Grid item md={12} xs={12} lg={12}>
                      {locations && (
                        <Typography variant="caption" color="gray" my={1}>
                          {t("pages.dsokProducts.stocks")}
                        </Typography>
                      )}
                      {Object.keys(locations || {})?.map((id) => {
                        const location = locations[id];
                        return (
                          <Box
                            key={id}
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            my={1}
                          >
                            <Grid item md={4} xs={4} lg={4}>
                              <Div sx={{ mt: 2, mb: 1, width: "50%" }}>
                                <JumboTextField
                                  type="number"
                                  fullWidth
                                  name={`stock.${location.code}`}
                                  value={values.stock?.[location.code] ?? ""}
                                  label={
                                    t("pages.dsokProducts.stock") +
                                    " " +
                                    location.name
                                  }
                                  InputLabelProps={{ required: false }}
                                  InputProps={{
                                    inputProps: {
                                      min: 0,
                                    },
                                    onChange: (e) =>
                                      setFieldValue(
                                        `stock.${location.code}`,
                                        e.target.value,
                                      ),
                                  }}
                                />
                              </Div>
                            </Grid>
                          </Box>
                        );
                      })}
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </Stack>
          </TabPanel>
          <TabPanel
            value="description"
            sx={{ p: 0, height: "calc(100vh - 260px)" }}
          >
            <TabContext value={selectedLanguage}>
              <Stack sx={{ mb: 2 }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <TabList
                    onChange={(_, val) => setSelectedLanguage(val)}
                    aria-label="lab API tabs example"
                  >
                    {productLanguages.map((lang) => {
                      return (
                        <Tab
                          key={lang}
                          label={t(`languages.${lang}`)}
                          value={lang}
                        />
                      );
                    })}
                  </TabList>
                </Box>
                {productLanguages.map((lang) => {
                  return (
                    <TabPanel
                      key={lang}
                      value={lang}
                      sx={{
                        pb: 1,
                        pr: 0,
                        height: "100%",
                        overflow: "auto",
                      }}
                    >
                      <Typography variant="caption" color="gray" my={1}>
                        {t("pages.products.description")}
                      </Typography>
                      <CKEditor
                        onChange={(event) => {
                          const data = event.editor.getData();
                          setFieldValue(`productDescription.${lang}`, data);
                        }}
                        initData={
                          (values.productDescription &&
                            values.productDescription[lang]) ||
                          ""
                        }
                        config={{
                          contentsCss: [
                            `${process.env.PUBLIC_URL}/fonts/noir-pro/styles.css`,
                            `${process.env.PUBLIC_URL}/vendors/ck-editor/style.css`,
                          ],
                          height: 400,
                          extraPlugins: "font,colorbutton,justify,colordialog",
                          fontSize_sizes: CK_FONT_SIZE_OPTIONS,
                          colorButton_enableMore: true,
                          versionCheck: false,
                        }}
                      />
                    </TabPanel>
                  );
                })}
              </Stack>
            </TabContext>
          </TabPanel>
          <TabPanel value="parameters" sx={{ p: 0, height: "100%" }}>
            <Stack width="100%" spacing={3} sx={{ py: 1 }}>
              <Box>
                <Typography
                  variant="caption"
                  color="gray"
                  sx={{ mt: 2, mb: 0.5, display: "block" }}
                >
                  {t("pages.dsokProducts.allergens")}
                </Typography>
                <FormGroup
                  row
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    columnGap: 1,
                    rowGap: 0.5,
                  }}
                >
                  {Object.values(allergensList)
                    .sort((a, b) => a.number - b.number)
                    .map(({ number, title }) => (
                      <FormControlLabel
                        key={number}
                        control={
                          <Checkbox
                            size="small"
                            checked={(values.allergens || []).includes(number)}
                            onChange={(e) => {
                              const current = Array.isArray(values.allergens)
                                ? values.allergens
                                : [];
                              const next = e.target.checked
                                ? Array.from(new Set([...current, number]))
                                : current.filter((n) => n !== number);
                              setFieldValue("allergens", next);
                            }}
                          />
                        }
                        label={`[${number}] ${title.sk}`}
                      />
                    ))}
                </FormGroup>
              </Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    {t("pages.dsokProducts.parameters")}
                  </Typography>
                  <ParametersSection
                    values={values}
                    setFieldValue={setFieldValue}
                    t={t}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <KitchenPrintingSection
                    values={values}
                    setFieldValue={setFieldValue}
                    t={t}
                    datavisionPosEnabled={datavisionPosEnabled}
                    kitchenPrintingEnabled={kitchenPrintingEnabled}
                    kitchenPrintersGrouped={kitchenPrintersGrouped || []}
                  />
                </Grid>
              </Grid>
            </Stack>
          </TabPanel>
          {accessSystemEnabled === true && (
            <TabPanel value="individualSettings" sx={{ p: 0, height: "100%" }}>
              <IndividualSettingsTab
                t={t}
                values={values}
                setFieldValue={setFieldValue}
                ticketPrintersGrouped={ticketPrintersGrouped || []}
                ticketPrintersLoading={ticketPrintersLoading}
              />
            </TabPanel>
          )}
        </Box>
      </TabContext>
    </Stack>
  );
};

export default MainTab;
